// What a dialog does, apart from how it is drawn.
//
// # WHY THIS IS NOT IN THE COMPONENT
//
// Where focus goes when a dialog opens, what Escape does during a save, where
// Tab goes from the last button: each of these is a decision, and a decision
// buried in an effect can only be checked in a browser. The console's tests
// run in node with no document. So the decisions are plain functions over
// plain descriptions (a field that is shown, a stop that is above the dialog),
// and the one object that acts on them, Frame, reaches the document through a
// small interface (Dom) the tests stand in for. Dialog.tsx draws the frame and
// hands the real document in.

// A form is a dialog whose fields are sent by its main action. A record shows
// a thing: an explanation, a finding, a risk acceptance, with whatever editors
// and decisions belong to it.
export type Kind = 'form' | 'record'

// ------------------------------------------------------------ first focus

export type Seen = {
  kind: Kind
  // The dialog asks before it closes (a key shown once).
  guarded: boolean
  // The question is on screen.
  asking: boolean
  fields: { shown: boolean; inView: boolean; off: boolean; radio: boolean; checked: boolean }[]
  main: { destructive: boolean; off: boolean } | null
  cancel: boolean
}

export type Spot = { spot: 'field'; field: number } | { spot: 'main' | 'cancel' | 'close' | 'heading' | 'stay' }

// Where the keyboard starts.
//
//  - A destructive main action: Cancel. Enter on whatever opened the dialog
//    must not be one more Enter away from the act.
//  - A dialog that asks before closing: its heading while it shows the thing
//    it guards, so no button is under a key that is still being pressed, and
//    the button that keeps it open once the question is asked.
//  - A record: Close. Its fields are editors that may save as they are left,
//    so focus is never put in one for the reader.
//  - A form: its first field, unless that is below the fold; with no field,
//    its main action.
export function firstFocus(s: Seen): Spot {
  if (s.asking) return { spot: 'stay' }
  if (s.guarded) return { spot: 'heading' }
  if (s.main?.destructive) return { spot: s.cancel ? 'cancel' : 'close' }
  if (s.kind === 'record') return { spot: 'close' }
  // A group of choices is entered at the one that is chosen.
  const first = s.fields.findIndex((f) => f.shown && !f.off && (!f.radio || f.checked))
  if (first >= 0) return s.fields[first]?.inView ? { spot: 'field', field: first } : { spot: 'close' }
  return s.main && !s.main.off ? { spot: 'main' } : { spot: 'close' }
}

// ---------------------------------------------------------------- leaving

export type Via = 'escape' | 'scrim' | 'close' | 'cancel'

// What a way out does.
//
// While a save is out, Close and Escape still close: nobody is held by a
// request that never answers. The scrim does not, so a stray press cannot lose
// the dialog, and Cancel does not, because nothing is cancelled: the request
// goes on and its answer is told in a toast (lib/save.ts).
//
// A dialog that guards something asks first, whichever way out is taken. With
// the question up, Escape takes the question away and the rest do nothing:
// the question has its own two buttons.
export function leavePlan(via: Via, s: { busy: boolean; guarded: boolean; asking: boolean }): 'close' | 'ask' | 'unask' | 'stay' {
  if (s.busy) return via === 'escape' || via === 'close' ? 'close' : 'stay'
  if (s.asking) return via === 'escape' ? 'unask' : 'stay'
  if (s.guarded) return 'ask'
  return 'close'
}

// The frame's own controls that close the dialog or back out of a step in it
// (Close, Cancel, "Close anyway") carry this mark. An editor that saves as it
// is left asks for it, so that focus going to one of them is leaving, with
// what was typed dropped, and not moving on to the next field. The mark is
// put on by the frame, once, so no caller has to know the frame's class names.
export const LEAVES = 'data-leaves-dialog'

export function leavesDialog(to: El): boolean {
  const marked = (to as { closest?: (selector: string) => unknown } | null)?.closest?.('[' + LEAVES + ']')
  return marked !== null && marked !== undefined
}

// ------------------------------------------------------------------- keys

// What a key does. Only the dialog on top answers, only on the key going
// down, and never on a key that was already down: a held Enter that opened the
// dialog would otherwise press whatever the dialog put under it. Holding a key
// in a box that takes text is typing, and is left alone.
export function keyPlan(e: { type: string; key: string; repeat: boolean; onText: boolean }, top: boolean): 'pass' | 'swallow' | 'escape' | 'tab' {
  if (!top || e.type !== 'keydown') return 'pass'
  if (e.repeat) {
    if (e.key === 'Escape') return 'swallow'
    return (e.key === 'Enter' || e.key === ' ') && !e.onText ? 'swallow' : 'pass'
  }
  if (e.key === 'Escape') return 'escape'
  return e.key === 'Tab' ? 'tab' : 'pass'
}

// Where Tab goes. The stops are the dialog's own, in order, and after them
// whatever is drawn above the dialog and can be pressed (a toast's buttons).
// Inside a run of stops the browser moves focus itself, which is what steps
// through the parts of a date. At an end, or from anywhere that is not a stop
// (the heading, the dialog's own background, the page), it is taken to the
// next stop round the ring, so it cannot leave.
export function tabPlan(stops: { shown: boolean; above: boolean }[], at: number, back: boolean): { pass: true } | { pass: false; to: number | 'panel' } {
  const ring = stops.map((s, i) => (s.shown ? i : -1)).filter((i) => i >= 0)
  if (ring.length === 0) return { pass: false, to: 'panel' }
  const first = ring[0] as number
  const last = ring[ring.length - 1] as number
  const pos = ring.indexOf(at)
  if (pos < 0) return { pass: false, to: back ? last : first }
  const next = ring[(pos + (back ? ring.length - 1 : 1)) % ring.length] as number
  const here = stops[at] as { above: boolean }
  // The two groups are apart in the document, so the step between them, and
  // every step among the ones above, is made by hand.
  if (here.above || (stops[next] as { above: boolean }).above || at === (back ? first : last)) return { pass: false, to: next }
  return { pass: true }
}

// -------------------------------------------------------------- the stack

// The dialogs that are open, the last one on top. A reason opened from inside
// a form is a second dialog over the first: only the top one answers the
// keyboard, and the ones under it are inert.
export function makeStack() {
  let open: symbol[] = []
  const heard = new Set<() => void>()
  const tell = () => heard.forEach((f) => f())
  return {
    join(id: symbol): void {
      open = [...open.filter((x) => x !== id), id]
      tell()
    },
    leave(id: symbol): void {
      open = open.filter((x) => x !== id)
      tell()
    },
    top: (id: symbol): boolean => open[open.length - 1] === id,
    // Open, with another dialog over it.
    under: (id: symbol): boolean => open.includes(id) && open[open.length - 1] !== id,
    size: (): number => open.length,
    subscribe(f: () => void): () => void {
      heard.add(f)
      return () => heard.delete(f)
    },
  }
}
export type Stack = ReturnType<typeof makeStack>

// ----------------------------------------------------------------- toasts

// How far from the bottom of the window the toasts keep while a dialog is
// open: above the top of its footer, whatever height the footer has grown to.
// A dialog with no footer leaves them where they are.
export function toastClear(viewport: number, footTop: number | null): number {
  return footTop === null ? 0 : Math.max(0, Math.round(viewport - footTop))
}

// -------------------------------------------------------- the document

export type El = unknown

export type Dom = {
  // What has focus, or null when nothing in the document has.
  active(): El | null
  holds(el: El): boolean
  attached(el: El): boolean
  focus(el: El): void
  seen(): Seen
  // The element a spot is.
  at(spot: Spot): El | null
  stops(): { el: El; shown: boolean; above: boolean }[]
  panel(): El
  listen(type: string, heard: (e: KeyLike) => void): () => void
  // The dialog's content changed.
  watch(heard: () => void): () => void
  // Focus going from one control to another: told true as it leaves for
  // something and false once it has arrived or has left for nothing. `never`
  // is a move that did not arrive, its target having gone meanwhile. Told
  // before the page's own handlers hear the same move, because one of them
  // may change the dialog in that instant.
  transit(heard: (under: boolean, never: boolean) => void): () => void
  // The page behind: inert and not scrolling, or as it was.
  page(behind: boolean): void
  footTop(): number | null
  viewport(): number
  clear(px: number): void
  now(): number
}

export type KeyLike = { type: string; key: string; repeat: boolean; shiftKey: boolean; target: El; preventDefault(): void }

export type Told = {
  // The dialog as it is now.
  state(): { kind: Kind; busy: boolean; guarded: boolean; asking: boolean }
  close(): void
  ask(on: boolean): void
  onText(target: El): boolean
}

// One open dialog: what it does when it opens, while it is open, and when it
// closes.
export class Frame {
  readonly id = Symbol('dialog')
  private dom: Dom | null = null
  private back: El | null = null
  private landed: El | null = null
  private fellBack = false
  private moving = false
  private since = 0
  private undo: (() => void)[] = []

  constructor(
    private readonly stack: Stack,
    private readonly told: Told,
    private readonly domOf: (anchor: El) => Dom,
  ) {}

  // The ref of a mark drawn inside the panel only while the dialog is open: it
  // arrives when the dialog opens and goes when it closes, so opening and
  // closing need no effect to remember to run.
  readonly live = (anchor: El | null): void => {
    if (anchor) this.enter(this.domOf(anchor))
    else this.leave()
  }

  private state(): Seen {
    return { ...(this.dom as Dom).seen(), ...this.told.state() }
  }

  private land(): void {
    const dom = this.dom as Dom
    const spot = firstFocus(this.state())
    if (spot.spot === 'stay') return
    const el = dom.at(spot) ?? dom.panel()
    this.landed = el
    this.fellBack = spot.spot === 'close' || spot.spot === 'heading'
    dom.focus(el)
  }

  private enter(dom: Dom): void {
    if (this.dom) this.leave()
    this.dom = dom
    const active = dom.active()
    this.back = active && !dom.holds(active) ? active : null
    if (this.stack.size() === 0) dom.page(true)
    this.stack.join(this.id)
    this.since = dom.now()
    this.land()
    dom.clear(toastClear(dom.viewport(), dom.footTop()))
    this.undo = [
      dom.listen('keydown', (e) => this.key(e)),
      dom.watch(() => this.changed()),
      dom.transit((under, never) => this.move(under, never)),
      // On top again once a dialog over this one has closed.
      this.stack.subscribe(() => this.changed()),
    ]
  }

  private leave(): void {
    const dom = this.dom
    if (!dom) return
    this.dom = null
    this.moving = false
    for (const f of this.undo.splice(0)) f()
    this.stack.leave(this.id)
    if (this.stack.size() === 0) {
      dom.page(false)
      dom.clear(0)
    }
    const back = this.back
    this.back = null
    if (back && dom.attached(back)) dom.focus(back)
  }

  // Two things can leave the keyboard in the wrong place after opening. A form
  // that reads its values before it draws its fields has none yet: when they
  // arrive focus moves to the first, unless the reader has moved already. And
  // a form that turns into its answer (a key, shown once) removes the field
  // focus was in, which would drop focus onto the page: it is brought back.
  changed(): void {
    const dom = this.dom
    if (!dom || !this.stack.top(this.id)) return
    dom.clear(toastClear(dom.viewport(), dom.footTop()))
    this.keep()
  }

  // Between one control losing focus and the next gaining it, nothing in the
  // document has focus. That is not focus lost. An editor that saves as it is
  // left changes the dialog in exactly that instant (its box, its line of
  // words), and taking the change for lost focus sent the keyboard to Close
  // instead of the control the reader had tabbed to. So nothing is judged
  // while focus is on its way. A move that never arrives has left focus
  // nowhere, and that is judged once, when it is known.
  private move(under: boolean, never: boolean): void {
    this.moving = under
    if (never) this.keep()
  }

  private keep(): void {
    const dom = this.dom
    if (!dom || !this.stack.top(this.id) || this.moving) return
    const at = dom.active()
    const lost = at === null || (!dom.holds(at) && !dom.stops().some((s) => s.el === at))
    const waiting = this.fellBack && at === this.landed && dom.now() - this.since < 3000
    if (!lost && !waiting) return
    const spot = firstFocus(this.state())
    if (spot.spot === 'stay') return
    const next = dom.at(spot) ?? dom.panel()
    if (next !== at) {
      this.landed = next
      dom.focus(next)
    }
  }

  readonly leaveBy = (via: Via): void => {
    const plan = leavePlan(via, this.told.state())
    if (plan === 'close') this.told.close()
    else if (plan === 'ask') this.told.ask(true)
    else if (plan === 'unask') this.told.ask(false)
  }

  readonly scrim = (): void => this.leaveBy('scrim')
  readonly closeButton = (): void => this.leaveBy('close')
  readonly cancel = (): void => this.leaveBy('cancel')

  key(e: KeyLike): void {
    const dom = this.dom
    if (!dom) return
    const plan = keyPlan({ type: e.type, key: e.key, repeat: e.repeat, onText: this.told.onText(e.target) }, this.stack.top(this.id))
    if (plan === 'pass') return
    if (plan === 'swallow') return e.preventDefault()
    if (plan === 'escape') {
      e.preventDefault()
      return this.leaveBy('escape')
    }
    const stops = dom.stops()
    const active = dom.active()
    const to = tabPlan(stops, stops.findIndex((s) => s.el === active), e.shiftKey)
    if (to.pass) return
    e.preventDefault()
    dom.focus(to.to === 'panel' ? dom.panel() : (stops[to.to] as { el: El }).el)
  }
}
